import React from 'react';

const About = () => {
	return (
		<>
			<title>About Us - FlickScout.com</title>
			<meta name="description" content="Learn about FlickScout - the AI-powered movie recommendation platform that helps you find the perfect movie to watch." />
			
			<div className="container mx-auto px-4 py-12">
				<div className="max-w-4xl mx-auto">
					<h1 className="text-4xl font-bold mb-8 text-center">About FlickScout</h1>
					
					<div className="bg-gray-800 rounded-lg p-8 mb-8 shadow-lg">
						<h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
						<p className="text-gray-300 mb-6">
							At FlickScout, we're on a mission to eliminate the endless scrolling through streaming platforms. 
							We believe finding the perfect movie should be as enjoyable as watching it.
						</p>

					</div>
					
					<div className="bg-gray-800 rounded-lg p-8 mb-8 shadow-lg">
						<h2 className="text-2xl font-semibold mb-4">How It Works</h2>
						<p className="text-gray-300 mb-4">
							FlickScout uses cutting-edge artificial intelligence to understand exactly what kind of movie you're looking for.
						</p>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
							<div className="text-center">
								<div className="bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
									<span className="text-2xl font-bold">1</span>
								</div>
								<h3 className="font-semibold mb-2">Describe</h3>
								<p className="text-gray-400">Tell us what you're in the mood for in natural language</p>
							</div>
							<div className="text-center">
								<div className="bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
									<span className="text-2xl font-bold">2</span>
								</div>
								<h3 className="font-semibold mb-2">Match</h3>
								<p className="text-gray-400">Our AI finds the perfect movie based on your description</p>
							</div>
							<div className="text-center">
								<div className="bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
									<span className="text-2xl font-bold">3</span>
								</div>
								<h3 className="font-semibold mb-2">Watch</h3>
								<p className="text-gray-400">See where you can stream your perfect match</p>
							</div>
						</div>
					</div>
					
					<div className="bg-gray-800 rounded-lg p-8 mb-8 shadow-lg">
						<h2 className="text-2xl font-semibold mb-4">The Technology</h2>
						<p className="text-gray-300 mb-4">
							FlickScout combines multiple cutting-edge technologies to deliver precise movie recommendations:
						</p>
						<div className="space-y-4">
							<div>
								<h3 className="text-xl font-semibold mb-2 text-yellow-400">Dual LLM Approach</h3>
								<p className="text-gray-300">
									We leverage two powerful Large Language Models (LLMs) - OpenAI and Groq - working in parallel to analyze your movie descriptions. 
									This dual approach ensures more accurate and diverse recommendations by combining the strengths of different AI models.
								</p>
							</div>
							<div>
								<h3 className="text-xl font-semibold mb-2 text-yellow-400">Vector Search Technology</h3>
								<p className="text-gray-300">
									Your movie descriptions are converted into mathematical vectors using embeddings, then matched against our 
									extensive movie database using Pinecone, a specialized vector database that enables semantic search beyond simple keyword matching.
								</p>
							</div>
							<div>
								<h3 className="text-xl font-semibold mb-2 text-yellow-400">Movie Data Integration</h3>
								<p className="text-gray-300">
									We integrate with TMDB (The Movie Database) to provide comprehensive information about each recommended movie, 
									including where you can stream it, cast information, ratings, and more.
								</p>
							</div>
							<div>
								<h3 className="text-xl font-semibold mb-2 text-yellow-400">User Feedback Loop</h3>
								<p className="text-gray-300">
									We store user interactions and feedback in Supabase to continuously improve our recommendation algorithm, 
									making it smarter with every search.
								</p>
							</div>
						</div>
					</div>
					
					<div className="bg-gray-800 rounded-lg p-8 mb-8 shadow-lg">
						<h2 className="text-2xl font-semibold mb-4">The Creator</h2>
						<div className="flex flex-col md:flex-row items-center md:items-start gap-6">
							<div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center">
								<span className="text-2xl font-bold">DV</span>
							</div>
							<div>
								<h3 className="text-xl font-semibold mb-2">Danilo Vaccalluzzo</h3>
								<p className="text-gray-300 mb-4">
									FlickScout was created by Danilo Vaccalluzzo, an expert in growth marketing and software development.
									Combining his technical expertise with strategic marketing insights, Danilo built FlickScout to solve
									the universal problem of finding the perfect movie to watch.
								</p>
								<a 
									href="https://www.linkedin.com/in/danilovaccalluzzo/" 
									target="_blank" 
									rel="noopener noreferrer"
									className="text-blue-400 hover:text-blue-300 flex items-center"
								>
									<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
										<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
									</svg>
									Connect on LinkedIn
								</a>
							</div>
						</div>
					</div>
					
					<div className="bg-gray-800 rounded-lg p-8 shadow-lg">
						<h2 className="text-2xl font-semibold mb-4">Support FlickScout</h2>
						<p className="text-gray-300 mb-6">
							FlickScout is a passion project built to help movie lovers find their perfect watch. If you find it valuable, 
							consider supporting its continued development and server costs.
						</p>
						
						<div className="flex justify-center">
							<a 
								href="https://github.com/sponsors/danilotrix86?o=esb" 
								target="_blank" 
								rel="noopener noreferrer"
								className="bg-gray-700 hover:bg-gray-600 transition-colors duration-300 rounded-lg p-6 flex flex-col items-center text-center max-w-xs"
							>
								<svg className="w-10 h-10 mb-4" viewBox="0 0 24 24" fill="currentColor">
									<path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
								</svg>
								<h3 className="font-semibold mb-2">GitHub Sponsors</h3>
								<p className="text-gray-400">Support development directly through GitHub</p>
							</a>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default About;
